import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CheckCheck,
  Settings as SettingsIcon,
  ShoppingCart,
  CreditCard,
  Store as StoreIcon,
  Package,
  UserCircle,
  X,
} from 'lucide-react';
import type { Notification, NotificationCategory } from '@cartiq/shared';
import {
  useNotifications,
  useUnreadCount,
  useMarkRead,
  useMarkAllRead,
  useDeleteNotification,
} from '../../api/notifications';
import { cn, formatRelativeTime } from '../../lib/utils';

const categoryIcon: Record<NotificationCategory, typeof Bell> = {
  orders: ShoppingCart,
  payments: CreditCard,
  store: StoreIcon,
  inventory: Package,
  account: UserCircle,
};

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const { data: feed, isLoading } = useNotifications();
  const { data: unread = 0 } = useUnreadCount();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const remove = useDeleteNotification();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const items = feed?.items ?? [];

  function handleClick(n: Notification) {
    if (!n.readAt) markRead.mutate(n.id);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-10 w-10 items-center justify-center rounded-lg text-mid transition-colors hover:bg-accent-light hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
        aria-expanded={open}
        aria-controls="cartiq-notifications"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-none text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div id="cartiq-notifications" className="fixed inset-x-4 top-[5.5rem] overflow-hidden rounded-xl border border-border bg-white shadow-float animate-pop-in sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[22rem] sm:max-w-[calc(100vw-2rem)]">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <p className="text-sm font-semibold text-primary">Notifications</p>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={() => markAllRead.mutate()}
                  className="flex items-center gap-1 rounded px-1.5 py-1 text-xs text-mid transition-colors hover:bg-surface hover:text-primary"
                  title="Mark all read"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => {
                  setOpen(false);
                  navigate('/settings/notifications');
                }}
                className="rounded p-1 text-mid transition-colors hover:bg-surface hover:text-primary"
                title="Notification settings"
              >
                <SettingsIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[26rem] overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-10 text-center text-sm text-mid">Loading…</div>
            ) : items.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="mx-auto mb-2 h-8 w-8 text-border" />
                <p className="text-sm text-mid">You're all caught up</p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((n) => {
                  const Icon = categoryIcon[n.category] ?? Bell;
                  return (
                    <li
                      key={n.id}
                      className={cn(
                        'group relative flex gap-3 px-4 py-3 transition-colors hover:bg-surface',
                        !n.readAt && 'bg-accent/[0.04]',
                      )}
                    >
                      <button
                        onClick={() => handleClick(n)}
                        className="flex flex-1 gap-3 text-left"
                      >
                        <span
                          className={cn(
                            'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                            n.readAt
                              ? 'bg-surface text-mid'
                              : 'bg-accent/10 text-accent',
                          )}
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span className="truncate text-sm font-medium text-primary">
                              {n.title}
                            </span>
                            {!n.readAt && (
                              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                            )}
                          </span>
                          <span className="mt-0.5 block text-xs leading-snug text-mid">
                            {n.body}
                          </span>
                          <span className="mt-1 block text-[11px] text-mid/70">
                            {formatRelativeTime(n.createdAt)}
                          </span>
                        </span>
                      </button>
                      <button
                        onClick={() => remove.mutate(n.id)}
                        className="absolute right-2 top-2 rounded p-1 text-mid opacity-0 transition-opacity hover:bg-border/50 hover:text-primary focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 group-hover:opacity-100 max-md:opacity-100"
                        aria-label="Dismiss notification"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <button
            onClick={() => {
              setOpen(false);
              navigate('/settings/notifications');
            }}
            className="block w-full border-t border-border px-4 py-2.5 text-center text-xs font-medium text-accent transition-colors hover:bg-surface"
          >
            Notification settings
          </button>
        </div>
      )}
    </div>
  );
}
